import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Paper,
  Avatar,
  Box,
  Typography,
  IconButton,
  Button,
  Slide,
  SlideProps,
} from '@mui/material';
import {
  Reply as ReplyIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { allTokens } from '@hopdrive/ui/tokens';
import { getElevation } from '../utils/elevation';
import { notificationService, type ToastNotificationOptions } from '../services/notificationService';

interface ChatNotificationState extends ToastNotificationOptions {
  id: string;
  open: boolean;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

const ChatNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<ChatNotificationState[]>([]);

  useEffect(() => {
    const handleToastNotification = (options: ToastNotificationOptions) => {
      const id = `notification-${Date.now()}-${Math.random()}`;
      const newNotification: ChatNotificationState = {
        ...options,
        id,
        open: true,
      };

      setNotifications(prev => [...prev, newNotification]);

      // Auto-dismiss after duration
      const duration = options.duration || 5000;
      setTimeout(() => {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === id ? { ...notif, open: false } : notif
          )
        );
      }, duration);

      // Remove from array after animation completes
      setTimeout(() => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
      }, duration + 300); // Additional 300ms for exit animation
    };

    notificationService.setToastCallback(handleToastNotification);

    return () => {
      notificationService.setToastCallback(() => {});
    };
  }, []);

  const handleClose = (id: string) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, open: false } : notif
      )
    );

    // Remove from array after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 300);
  };

  const handleAction = (notification: ChatNotificationState) => {
    if (notification.action?.onClick) {
      notification.action.onClick();
    }
    handleClose(notification.id);
  };

  return (
    <>
      {children}
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={notification.open}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={SlideTransition}
          sx={{
            zIndex: (theme) => theme.zIndex.snackbar,
          }}
        >
          <Paper
            sx={{
              width: 360,
              maxWidth: 'calc(100vw - 48px)',
              borderRadius: `${allTokens.radius.lg}px`,
              boxShadow: getElevation(allTokens.elevation.level2),
              overflow: 'hidden',
              p: 2.5,
            }}
          >
            {/* Header: avatar + title + dismiss */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              {/* Avatar */}
              {notification.avatar || notification.sender ? (
                <Avatar
                  src={notification.avatar}
                  sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}
                >
                  {notification.sender?.name?.[0]?.toUpperCase() ||
                   notification.sender?.id?.[0]?.toUpperCase() ||
                   <ChatIcon sx={{ fontSize: 18 }} />}
                </Avatar>
              ) : (
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main', color: '#fff' }}>
                  <ChatIcon sx={{ fontSize: 18 }} />
                </Avatar>
              )}

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                  {notification.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {notification.body}
                </Typography>
                {notification.timestamp && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {notification.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                )}
              </Box>

              {/* Dismiss */}
              <IconButton
                size="small"
                onClick={() => handleClose(notification.id)}
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Action button */}
            {notification.action && (
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleClose(notification.id)}
                  sx={{ flex: 1, textTransform: 'none' }}
                >
                  Dismiss
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ReplyIcon />}
                  onClick={() => handleAction(notification)}
                  sx={{ flex: 1, textTransform: 'none' }}
                >
                  {notification.action.label}
                </Button>
              </Box>
            )}
          </Paper>
        </Snackbar>
      ))}
    </>
  );
};

export default ChatNotificationProvider;
